import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SocialPlatform, type FacebookPage } from '@mg/shared';
import { Check, ExternalLink } from 'lucide-react';
import { api, ApiRequestError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { FacebookIcon } from './brand-icons';
import { PLATFORM_META } from './platforms';

/**
 * "Choose your Facebook Page" picker, shown after the user returns from Facebook
 * (or to finish a pending connection). Loads the manageable Pages, lets the user
 * pick one, and persists the selection as the connected Facebook account.
 */
export function FacebookPagePickerModal({
  open,
  tenantKey,
  connectionId,
  onClose,
  onConnected,
}: {
  open: boolean;
  tenantKey: string;
  connectionId: string | null;
  onClose: () => void;
  onConnected: () => void;
}) {
  const queryClient = useQueryClient();

  const pages = useQuery({
    queryKey: ['facebook-pages', tenantKey, connectionId],
    queryFn: ({ signal }) => api.facebookPages(connectionId as string, signal),
    enabled: open && connectionId != null,
    staleTime: 0,
    gcTime: 0,
  });

  const select = useMutation({
    mutationFn: (pageId: string) => api.facebookSelectPage(connectionId as string, pageId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['social-connections', tenantKey] });
      onConnected();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Choose your Facebook Page"
      footer={
        <Button variant="secondary" onClick={onClose} disabled={select.isPending}>
          Cancel
        </Button>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-ink-muted">
          Pick the Facebook Page you&apos;d like to connect. We&apos;ll monitor its activity and let
          you publish to it.
        </p>

        {pages.isLoading ? (
          <LoadingState label="Loading your Facebook Pages…" />
        ) : pages.isError ? (
          <ErrorState
            message={
              pages.error instanceof ApiRequestError
                ? pages.error.message
                : 'Could not load your Facebook Pages.'
            }
            onRetry={() => void pages.refetch()}
          />
        ) : (pages.data ?? []).length === 0 ? (
          <EmptyState
            title="No Facebook Pages found"
            description="No Facebook Pages found on your account. Create a Page first, then reconnect."
            action={
              <a
                href="/guide/facebook-page"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-link hover:text-action-hover"
              >
                How to create a Facebook Page <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
            }
          />
        ) : (
          <ul className="space-y-2">
            {(pages.data ?? []).map((page) => (
              <PageRow
                key={page.id}
                page={page}
                selecting={select.isPending && select.variables === page.id}
                disabled={select.isPending}
                onSelect={() => select.mutate(page.id)}
              />
            ))}
          </ul>
        )}

        {select.isError && (
          <p className="text-sm text-red-600 dark:text-error-dark">
            {select.error instanceof ApiRequestError
              ? select.error.message
              : 'Could not connect that Page. Please try again.'}
          </p>
        )}
      </div>
    </Modal>
  );
}

function PageRow({
  page,
  selecting,
  disabled,
  onSelect,
}: {
  page: FacebookPage;
  selecting: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        disabled={disabled}
        className="focus-ring flex w-full items-center gap-3 rounded-xl border border-line bg-card px-3 py-2.5 text-left transition-colors hover:border-ink-ghost hover:bg-tint disabled:cursor-not-allowed disabled:opacity-60"
      >
        {page.pictureUrl ? (
          <img
            src={page.pictureUrl}
            alt=""
            width={40}
            height={40}
            className="h-10 w-10 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
              PLATFORM_META[SocialPlatform.FACEBOOK].tile,
            )}
          >
            <FacebookIcon className="h-5 w-5" />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-ink">{page.name}</span>
          {page.category && (
            <span className="block truncate text-xs text-ink-muted">{page.category}</span>
          )}
        </span>
        {selecting ? (
          <span className="text-xs font-medium text-action">Connecting…</span>
        ) : (
          <Check className="h-5 w-5 shrink-0 text-ink-ghost" aria-hidden />
        )}
      </button>
    </li>
  );
}
